import React, { useState } from "react";
import "./CollegeCardStyles.scss";
import { College, Course } from "../../../util/types";

interface CutOff {
  courseName: string;
  closingRank: string;
  openingRank: string;
  caste: string;
  gender: string;
  counsellingName: string;
  year: string;
  fee: string;
  seats: number;
  lastYearCutoffAiRank: number;
}

const CutoffModal: React.FC<{
  cutOff: CutOff[];
  onClose: () => void;
  selectedCourse: string;
  userRank?: string;
  probability?: string;
}> = ({ cutOff, onClose, selectedCourse, userRank, probability }) => {
  const selectedCutOffs = cutOff || [];

  const cutOffsByCategory: {
    [key: string]: {
      gender: string;
      caste: string;
      counsellingName: string;
      openingRank: string;
      closingRank: string;
      year: string;
      fee: string;
      seats: number;
      lastYearCutoffAiRank: number;
    };
  } = {};

  selectedCutOffs.forEach((cutoff) => {
    const categoryKey = `${cutoff.gender || "Neutral"}-${
      cutoff.caste || "Unknown"
    }-${cutoff.counsellingName || "Unknown"}`;
    cutOffsByCategory[categoryKey] = {
      gender: cutoff.gender || "Neutral",
      caste: cutoff.caste || "Unknown",
      counsellingName: cutoff.counsellingName || "Unknown",
      openingRank: cutoff.openingRank || "N/A",
      closingRank: cutoff.closingRank || "N/A",
      year: cutoff.year,
      fee: cutoff.fee || "N/A",
      seats: cutoff.seats || 0,
      lastYearCutoffAiRank: cutoff.lastYearCutoffAiRank || 0,
    };
  });

  const categories = Object.keys(cutOffsByCategory);

  console.log("CutoffModal Data:", {
    selectedCutOffs,
    cutOffsByCategory,
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center w-full h-full">
      <div
        className="absolute inset-0 bg-black opacity-50 z-40"
        onClick={onClose}
      ></div>
      <div className="relative z-50 bg-white rounded-lg shadow-lg w-11/12 sm:w-3/4 md:w-2/3 lg:w-3/4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-semibold">
            Round-wise Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          <h3 className="text-lg sm:text-xl font-bold">{selectedCourse}</h3>
          <div className="mt-4">
            <p className="text-sm font-semibold">
              Probability of getting this branch
            </p>
            <span className="inline-block mt-1 px-3 py-1 bg-green-200 text-green-800 rounded-full text-sm">
              {probability || "N/A"}
            </span>
          </div>
          <div className="mt-4">
            <h4 className="text-sm font-semibold">Category-wise Ranks</h4>
            <p className="text-sm mt-2">Your Rank - {userRank || "N/A"}</p>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full bg-white text-xs sm:text-sm border border-[#A3A2AC] college-predictor-table">
              <thead className="sticky top-0 bg-white border-b">
                <tr>
                  <th className="px-2 sm:px-4 py-2 whitespace-nowrap">
                    Gender
                  </th>
                  <th className="px-2 sm:px-4 py-2 whitespace-nowrap">
                    Category
                  </th>
                  <th className="px-2 sm:px-4 py-2 whitespace-nowrap">
                    Counselling
                  </th>
                  <th className="px-2 sm:px-4 py-2 whitespace-nowrap">
                    Opening Rank
                  </th>
                  <th className="px-2 sm:px-4 py-2 whitespace-nowrap">
                    Closing Rank
                  </th>
                  <th className="px-2 sm:px-4 py-2 whitespace-nowrap">Year</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((categoryKey) => {
                  const {
                    gender,
                    caste,
                    counsellingName,
                    openingRank,
                    closingRank,
                    year,
                  } = cutOffsByCategory[categoryKey];
                  return (
                    <tr key={categoryKey} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-4 py-2 whitespace-nowrap">
                        {gender}
                      </td>
                      <td className="px-2 sm:px-4 py-2 whitespace-nowrap">
                        {caste}
                      </td>
                      <td className="px-2 sm:px-4 py-2 whitespace-nowrap">
                        {counsellingName}
                      </td>
                      <td className="px-2 sm:px-4 py-2 whitespace-nowrap">
                        {openingRank}
                      </td>
                      <td className="px-2 sm:px-4 py-2 whitespace-nowrap">
                        {closingRank}
                      </td>
                      <td className="px-2 sm:px-4 py-2 whitespace-nowrap">
                        {year}
                      </td>
                    </tr>
                  );
                })}
                {categories.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-2 sm:px-4 py-2 text-center">
                      No cutoff data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CollegeCardProps {
  college: College;
  userRank?: string;
  allCourses?: any[];
}

const CollegeCard: React.FC<CollegeCardProps> = ({
  college,
  userRank,
  allCourses = [],
}) => {
  const [isCutoffModalOpen, setCutoffModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);

  const handleCutOff = (courseTitle: string) => {
    setSelectedCourse(courseTitle);
    setCutoffModalOpen(true);
  };

  const getCutOffData = (courseTitle: string): CutOff[] => {
    const cutOffData = allCourses
      .filter((course) => course.courseTitle === courseTitle)
      .map((course) => ({
        courseName: course.courseName,
        closingRank: course.closingRank.toString(),
        openingRank: course.openingRank.toString(),
        caste: course.caste || "Unknown",
        gender: course.gender || "Neutral",
        counsellingName: course.counsellingName || "Unknown",
        year: course.year || new Date().getFullYear().toString(),
        fee: course.fee || "N/A",
        seats: course.seats || 0,
        lastYearCutoffAiRank: course.lastYearCutoffAiRank || 0,
      }));
    console.log("getCutOffData for", courseTitle, ":", cutOffData);
    return cutOffData;
  };

  return (
    <div className="w-full bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white text-xs sm:text-sm border border-[#A3A2AC] college-predictor-table">
          <thead className="bg-[#E2F3FF] text-blue text-left">
            <tr>
              <th className="px-2 sm:px-4 py-2 whitespace-nowrap">College</th>
              <th className="px-2 sm:px-4 py-2 whitespace-nowrap">Branch</th>
              <th className="px-2 sm:px-4 py-2 whitespace-nowrap">
                Opening Rank
              </th>
              <th className="px-2 sm:px-4 py-2 whitespace-nowrap">
                Closing Rank
              </th>
              <th className="px-2 sm:px-4 py-2 whitespace-nowrap">
                Probability
              </th>
              <th className="px-2 sm:px-4 py-2 whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody>
            {college.courseVo.map((course: any, index: any) => (
              <tr key={course.courseName || index} className="hover:bg-gray-50">
                {index === 0 && (
                  <td
                    className="px-2 sm:px-4 py-2 border align-top"
                    rowSpan={college.courseVo.length}
                  >
                    <div className="flex flex-col gap-2 min-w-[200px]">
                      <span className="text-base sm:text-xl font-bold">
                        {college.collegeTitle}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-600">
                        { college.location || "Location Not Available"}
                      </span>
                    </div>
                  </td>
                )}
                <td className="px-2 sm:px-4 py-2">{course.courseTitle}</td>
                <td className="px-2 sm:px-4 py-2 text-right">
                  {course.openingRank !== -1 ? course.openingRank : "N/A"}
                </td>
                <td className="px-2 sm:px-4 py-2 text-right">
                  {course.closingRank !== -1 ? course.closingRank : "N/A"}
                </td>
                <td className="px-2 sm:px-4 py-2 text-center">
                  <span className="flex bg-green py-1 px-2 rounded-sm text-center items-center justify-center text-white w-12 max-w-12 mx-auto">
                    {course.probability || "N/A"}
                  </span>
                </td>
                <td className="px-2 sm:px-4 py-2">
                  <button
                    onClick={() => handleCutOff(course.courseTitle)}
                    className="text-blue hover:underline text-xs sm:text-sm whitespace-nowrap"
                  >
                    Round-wise Details
                  </button>
                </td>
              </tr>
            ))}
            {college.courseVo.length === 0 && (
              <tr>
                <td colSpan={6} className="px-2 sm:px-4 py-2 text-center">
                  No branches available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isCutoffModalOpen && selectedCourse && (
        <CutoffModal
          cutOff={getCutOffData(selectedCourse)}
          onClose={() => setCutoffModalOpen(false)}
          selectedCourse={selectedCourse}
          userRank={userRank}
          probability={
            college.courseVo.find((c) => c.courseTitle === selectedCourse)
              ?.probability
          }
        />
      )}
    </div>
  );
};

export default CollegeCard;
